import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { useApi } from '@/hooks/api';
import { useLocale } from '@/hooks/locale/useLocale';
import { useUserState } from '@/hooks/store';
import { calmLogger } from '@/utils/calmLogger';
import redirect from '@/utils/legacy/redirect';
import { JSONObject } from '@/utils/types';

import { AmexContextProps, AmexContextProviderProps, Offer } from './types';

export const AmexContext = createContext<AmexContextProps>({
	amexTermsUrl: '',
	faqUrl: '',
	initialOffer: null,
	isAmexGlobal: false,
	isAmexLoading: true,
	isGlobalOffer: false,
	isIneligible: false,
	liveMode: false,
	newOffer: null,
	setIsGlobalOffer: () => {},
	setNewOffer: () => {},
});

const AmexContextProvider = ({ children, isAmexGlobal }: AmexContextProviderProps) => {
	const user = useUserState();
	const { calmLanguage } = useLocale();
	const [initialOffer, setInitialOffer] = useState<Offer | null>(null);
	const [newOffer, setNewOffer] = useState<Offer | null>(null);
	const [liveMode, setLiveMode] = useState(false);
	const [isAmexLoading, setIsAmexLoading] = useState(true);
	const [isIneligible, setIsIneligible] = useState(false);
	const [isGlobalOffer, setIsGlobalOffer] = useState(false);
	const apiRequest = useApi();

	async function getUserOffer(): Promise<void> {
		try {
			const { data } = await apiRequest({
				endpoint: isAmexGlobal ? 'redeem/amexglobal' : 'redeem/amex',
				method: 'GET',
			});
			if (data?.status === 'hidden' && isAmexGlobal) {
				redirect('/404');
			} else {
				setInitialOffer(data as Offer);
				const promotion: JSONObject | undefined = data?.promotion as JSONObject;
				setLiveMode(promotion?.liveMode as boolean);
				setIsGlobalOffer(promotion?.isGlobal as boolean);
				setIsAmexLoading(false);
			}
		} catch (err) {
			setInitialOffer(null);
			setIsAmexLoading(false);
		}
	}

	const getFaqURL = useCallback(() => {
		if (calmLanguage === 'de') {
			return 'https://support.calm.com/hc/de/categories/360003785754-Angebot-f%C3%BCr-American-Express-Mitglieder';
		}

		if (calmLanguage === 'fr') {
			return 'https://support.calm.com/hc/fr/categories/360003785754-Offre-r%C3%A9serv%C3%A9e-aux-titulaires-de-la-carte-American-Express';
		}

		if (calmLanguage === 'es') {
			return 'https://support.calm.com/hc/es-419/categories/360003785754-Oferta-para-titulares-de-tarjetas-American-Express';
		}

		return 'https://support.calm.com/hc/en-us/categories/360003785754';
	}, [calmLanguage]);

	const getAmexTermsUrl = useCallback(() => {
		if (!isAmexGlobal) {
			return '/terms-amex?country=Generic&language=en&url=%2Fterms-amex';
		}

		if (calmLanguage === 'de') {
			return '/terms-amex-global?country=DEU&language=de&url=%2Fterms-amex-global';
		}

		if (calmLanguage === 'fr') {
			return '/terms-amex-global?country=FRA&language=fr&url=%2Fterms-amex-global';
		}

		if (calmLanguage === 'es') {
			return '/terms-amex-global?country=ESP&language=es&url=%2Fterms-amex-global';
		}

		return '/terms-amex-global?country=Generic&language=en&url=%2Fterms-amex-global';
	}, [calmLanguage, isAmexGlobal]);

	useEffect(() => {
		const isEligible = user?.subscription?.will_renew || user?.subscription?.is_lifetime;
		setIsIneligible(Boolean(isEligible));
	}, [user]);

	useEffect(
		() => {
			getUserOffer().catch(err => calmLogger.error('Error in AmexContextProvider getUserOffer', {}, err));
		}, // TODO [WEB-1595]: Fix this exhaustive-deps issue
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[],
	);

	const value = useMemo(
		() => ({
			newOffer,
			initialOffer,
			setNewOffer,
			faqUrl: getFaqURL(),
			amexTermsUrl: getAmexTermsUrl(),
			liveMode,
			isAmexLoading,
			isIneligible,
			isAmexGlobal,
			isGlobalOffer,
			setIsGlobalOffer,
		}),
		[
			newOffer,
			initialOffer,
			getFaqURL,
			getAmexTermsUrl,
			liveMode,
			isAmexLoading,
			isIneligible,
			isAmexGlobal,
			isGlobalOffer,
		],
	);

	return <AmexContext.Provider value={value}>{children}</AmexContext.Provider>;
};

export default AmexContextProvider;
